/**
 * `createTxDraft` (W1-03 core-model-ops; ARCHITECTURE.md §5.3): THE TxBuilder implementation.
 *
 * Pure core, so command bodies, tools, templates and their tests run anywhere (main thread, workers, Node).
 * A draft is an overlay over a container: `tx.doc` / `tx.idx` read through to the container and see every pending
 * write (overlay tables and copy-on-write index layers), while the container itself is untouched until the DocStore
 * applies `finish().ops`. On top of the overlay:
 *  - `update` applies SHADOW_RULES (editing a modelled field drops the provenance it shadows) and records only the
 *    fields that really change (a no-op update records nothing); `updateSingleton('pkg', ...)` applies the pkg rule;
 *  - `newId` hands out ids from the document counter (skipping any id already in use), advancing `tx.doc.idSeq`;
 *    the result ends with ONE idSeq op when the counter moved; `insert` refuses ids that were not handed out;
 *  - `remove` of a record that is already gone is a no-op; `update` of a missing record throws (programming error);
 *  - `applyOps` records explicit ops after checking each against the draft (`prev` / `rec` must match, else
 *    `fail({ key: 'ops.stale' })`); no shadow rules; idSeq ops and inserted ids only ever raise the counter;
 *  - `tempo` is the container's (cached) tempo map until the transaction changes the BPM table, then rebuilt lazily;
 *  - `fail` aborts the whole draft (nothing is recorded) and makes `run` return the reason, even when the command
 *    swallows the TxFailure and carries on (its later writes throw, and `run` still returns the first reason);
 *  - every value written (`insert`, `update`, `updateSingleton`, `applyOps`) has -0 mapped to 0 before it is compared
 *    or recorded, so a document never holds -0 (core/ops/equal.ts) and negating 0 records nothing.
 * After `finish` the draft's views stay readable (the DocStore may validate against them); writes throw.
 */

import type { TempoMap } from '../time/types';
import type { MessageRef } from '../util/types';
import { matchesFields, prevOf, valueEquals, withoutNegativeZero } from '../ops/equal';
import { applyShadowRules } from '../ops/shadow';
import type {
  CreateTxDraft,
  DocContainer,
  Op,
  RecordPatch,
  SelectionSnapshot,
  TxBuilder,
  TxDraft,
  TxDraftResult,
  TxnFocus,
} from '../ops/types';
import type { OrderListName, SingletonName, SingletonRecords, TableIds, TableName, TableRecords } from '../model/types';
import { applyOp } from './apply';
import { buildTempoMap, containerTempo, coreOf, createContainer, touchesBpm, type ContainerCore } from './container';
import { anyTable, createOverlayState, TABLES, type DocState } from './state';

/** Thrown by `tx.fail`; caught by `run` of the draft that threw it. */
export class TxFailure extends Error {
  override readonly name = 'TxFailure';
  readonly reason: MessageRef;
  readonly draft: object;

  constructor(reason: MessageRef, draft: object) {
    super(`transaction failed: ${reason.key}`);
    this.reason = reason;
    this.draft = draft;
  }
}

/** Misuse of a draft (writing after finish/abort, patching a missing record, ...). */
export class TxDraftError extends Error {
  override readonly name = 'TxDraftError';
}

/** The reason `applyOps` fails with when an op does not match the draft. */
export const STALE_OPS: MessageRef = Object.freeze({ key: 'ops.stale' });

const NUMERIC_TABLES: readonly TableName[] = TABLES.filter((t) => t !== 'assets');

/** A TxDraft with the selection it was created with. */
export interface TxDraftImpl extends TxDraft {
  readonly selBefore: SelectionSnapshot;
  /** 'open' while recording; 'finished' after `finish`; 'aborted' after `abort` or `fail`. */
  readonly status: 'open' | 'finished' | 'aborted';
}

function emptyResult(): TxDraftResult {
  return { ops: [], notes: [], focus: null, selection: null, rounding: { rounded: 0 } };
}

export const createTxDraft: CreateTxDraft = (c, sel) => createDraft(c, sel);

/** `createTxDraft` typed with the extra fields of the implementation. */
export function createDraft(c: DocContainer, sel: SelectionSnapshot): TxDraftImpl {
  const core: ContainerCore = coreOf(c) ?? (coreOf(createContainer(c.doc)) as ContainerCore);
  const state: DocState = createOverlayState(core.state);
  const idx = core.idx.overlay(state);
  const token = {};
  const idSeqStart = state.idSeq;
  let recorded: Op[] = [];
  let notes: MessageRef[] = [];
  let focus: TxnFocus | null = null;
  let selection: Partial<SelectionSnapshot> | null = null;
  const rounding = { rounded: 0 };
  let status: 'open' | 'finished' | 'aborted' = 'open';
  let failReason: MessageRef | null = null;
  let bpmVersion = 0;
  let tempoCache: { readonly version: number; readonly map: TempoMap } | null = null;

  const assertOpen = (what: string): void => {
    if (status !== 'open') throw new TxDraftError(`tx.${what}: the draft is ${status}`);
  };

  const record = (op: Op): void => {
    applyOp(state, idx, op);
    if (touchesBpm(op)) bpmVersion++;
    recorded.push(op);
  };

  const idInUse = (id: number): boolean => {
    for (const t of NUMERIC_TABLES) if ((state[t] as ReadonlyMap<number, unknown>).has(id)) return true;
    return false;
  };

  const abortNow = (): void => {
    status = 'aborted';
    recorded = [];
    notes = [];
    focus = null;
    selection = null;
  };

  const fail = (reason: MessageRef): never => {
    if (status === 'open') {
      abortNow();
      failReason = reason;
    }
    throw new TxFailure(reason, token);
  };

  const tempo = (): TempoMap => {
    if (bpmVersion === 0) return containerTempo(core);
    if (tempoCache?.version !== bpmVersion) tempoCache = { version: bpmVersion, map: buildTempoMap(state, idx) };
    return tempoCache.map;
  };

  const tx: TxBuilder = {
    get doc() {
      return state;
    },
    get idx() {
      return idx;
    },
    get tempo() {
      return tempo();
    },
    get rounding() {
      return rounding;
    },
    newId: <K extends Exclude<TableName, 'assets'>>(_tbl: K): TableIds[K] => {
      assertOpen('newId');
      let id = state.idSeq;
      while (idInUse(id)) id++;
      state.idSeq = id + 1;
      return id as TableIds[K];
    },
    insert: <T extends TableName>(tbl: T, rec: TableRecords[T]): void => {
      assertOpen('insert');
      if (tbl !== 'assets' && typeof rec.id === 'number' && rec.id >= state.idSeq) {
        throw new TxDraftError(`tx.insert: ${tbl} id ${String(rec.id)} was not handed out by tx.newId`);
      }
      record({ o: 'ins', tbl, rec: withoutNegativeZero(rec) } as Op);
    },
    update: <T extends TableName>(tbl: T, id: TableIds[T], patch: RecordPatch<TableRecords[T]>): void => {
      assertOpen('update');
      const cur = anyTable(state, tbl).get(id);
      if (cur === undefined) throw new TxDraftError(`tx.update: ${tbl} record ${String(id)} does not exist`);
      const shadowed = applyShadowRules(tbl, cur, withoutNegativeZero(patch));
      const { prev, next } = prevOf(cur, shadowed, true);
      if (Object.keys(next).length === 0) return;
      if (Object.prototype.hasOwnProperty.call(next, 'id'))
        throw new TxDraftError(`tx.update: cannot change the id of ${tbl} record ${String(id)}`);
      record({ o: 'set', tbl, id, prev, next } as Op);
    },
    remove: <T extends TableName>(tbl: T, id: TableIds[T]): void => {
      assertOpen('remove');
      const cur = anyTable(state, tbl).get(id);
      if (cur === undefined) return;
      record({ o: 'del', tbl, rec: cur } as Op);
    },
    updateSingleton: <S extends SingletonName>(key: S, patch: RecordPatch<SingletonRecords[S]>): void => {
      assertOpen('updateSingleton');
      const cur = state[key] as SingletonRecords[S];
      const clean = withoutNegativeZero(patch);
      const shadowed = key === 'pkg' ? applyShadowRules('pkg', cur, clean) : clean;
      const { prev, next } = prevOf(cur, shadowed, true);
      if (Object.keys(next).length === 0) return;
      record({ o: 'single', key, prev, next });
    },
    setOrder: (list: OrderListName, next: readonly number[]): void => {
      assertOpen('setOrder');
      const prev = state[list] as readonly number[];
      if (valueEquals(prev, next)) return;
      record({ o: 'order', list, prev, next: Object.freeze([...next]) });
    },
    applyOps: (ops: readonly Op[]): void => {
      assertOpen('applyOps');
      let maxId = -1;
      for (const raw of ops) {
        const op = withoutNegativeZero(raw);
        if (!matchesDraft(state, op)) fail(STALE_OPS);
        if (op.o === 'idSeq') {
          if (op.next > state.idSeq) state.idSeq = op.next;
          continue;
        }
        if (op.o === 'ins' && op.tbl !== 'assets' && typeof op.rec.id === 'number') maxId = Math.max(maxId, op.rec.id);
        record(op);
      }
      if (maxId >= state.idSeq) state.idSeq = maxId + 1;
    },
    fail,
    note: (msg: MessageRef): void => {
      assertOpen('note');
      notes.push(msg);
    },
    setFocus: (f: TxnFocus): void => {
      assertOpen('setFocus');
      focus = { ...f };
    },
    setSelection: (s: Partial<SelectionSnapshot>): void => {
      assertOpen('setSelection');
      selection = { ...(selection ?? {}), ...s };
    },
  };

  const draft: TxDraftImpl = {
    tx,
    selBefore: sel,
    get status() {
      return status;
    },
    run: <R>(build: (tx: TxBuilder) => R) => {
      assertOpen('run');
      try {
        const value = build(tx);
        if (failReason !== null) return { ok: false, reason: failReason } as const;
        return { ok: true, value } as const;
      } catch (e) {
        // Once `tx.fail` has aborted the draft, the transaction's outcome is that (first) reason, whatever the
        // command does afterwards: a command that swallows the TxFailure and writes again gets a TxDraftError
        // ("the draft is aborted"), which must not turn the fail toast into a crash of the DocStore.
        if (failReason !== null) return { ok: false, reason: failReason } as const;
        if (e instanceof TxFailure && e.draft === token) return { ok: false, reason: e.reason } as const;
        if (status === 'open') abortNow();
        throw e;
      }
    },
    finish: (): TxDraftResult => {
      if (status === 'aborted') return emptyResult();
      if (status === 'finished') throw new TxDraftError('finish: the draft is already finished');
      status = 'finished';
      const ops = recorded.slice();
      if (state.idSeq > idSeqStart) ops.push({ o: 'idSeq', next: state.idSeq });
      return { ops, notes: notes.slice(), focus, selection, rounding: { rounded: rounding.rounded } };
    },
    abort: (): void => {
      if (status === 'open') abortNow();
    },
  };
  return draft;
}

/** True when `op` can be applied to the draft as recorded (its `prev` / `rec` match the current state). */
function matchesDraft(state: DocState, op: Op): boolean {
  switch (op.o) {
    case 'ins':
      return !anyTable(state, op.tbl).has(op.rec.id);
    case 'del': {
      const cur = anyTable(state, op.tbl).get(op.rec.id);
      return cur !== undefined && (cur === op.rec || valueEquals(cur, op.rec));
    }
    case 'set': {
      const cur = anyTable(state, op.tbl).get(op.id);
      return cur !== undefined && matchesFields(cur, op.prev);
    }
    case 'single':
      return matchesFields(state[op.key], op.prev);
    case 'order':
      return valueEquals(state[op.list], op.prev);
    case 'idSeq':
      return true;
  }
}
